import { Plus } from "lucide-react";

import { trackEvent } from "@needl-ai/common/analytics";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@needl-ai/common/components/ui/tooltip";

interface NewChatButtonProps {
  onNewChat?: () => void;
  hasActiveConversation?: boolean;
  isStreaming?: boolean;
  isLoading?: boolean;
  hasIncompleteAttachments?: boolean;
  isCollapsed?: boolean;
}

const NewChatButton = ({
  onNewChat,
  hasActiveConversation = true,
  isStreaming = false,
  isLoading = false,
  hasIncompleteAttachments = false,
  isCollapsed = false,
}: NewChatButtonProps) => {
  const isDisabled =
    !hasActiveConversation ||
    isStreaming ||
    isLoading ||
    hasIncompleteAttachments;

  const handleNewChat = () => {
    trackEvent("new_chat_clicked");
    onNewChat?.();
  };

  const buttonClass = !isDisabled
    ? `${
        isCollapsed
          ? "w-12 h-12 rounded-lg flex items-center justify-center"
          : "w-full h-12 rounded-lg flex items-center gap-4 px-4"
      } transition-colors font-normal hover:font-medium active:font-medium text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 active:text-gray-900 dark:text-gray-200 dark:hover:bg-neutral-800 dark:hover:text-white dark:active:text-white`
    : `${
        isCollapsed
          ? "w-12 h-12 rounded-lg flex items-center justify-center"
          : "w-full h-12 rounded-lg flex items-center gap-4 px-4"
      } transition-colors font-normal text-sm opacity-75 cursor-not-allowed text-gray-400`;

  const tooltipText =
    isStreaming || isLoading
      ? "Please wait while processing..."
      : hasIncompleteAttachments
        ? "Complete file uploads before starting new chat"
        : "Start a new chat";

  if (isCollapsed) {
    return (
      <div className="px-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={isDisabled ? undefined : handleNewChat}
                className={buttonClass}
                disabled={isDisabled}
                aria-label={tooltipText}
                data-testid="new-chat-button"
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    !isDisabled
                      ? "bg-gray-200 hover:bg-gray-300 active:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:active:bg-gray-500"
                      : "bg-gray-300 dark:bg-gray-700"
                  }`}
                >
                  <Plus
                    size={16}
                    className={
                      !isDisabled
                        ? "text-gray-700 dark:text-white"
                        : "text-gray-500"
                    }
                  />
                </div>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div className="px-2">
      <button
        onClick={isDisabled ? undefined : handleNewChat}
        className={buttonClass}
        disabled={isDisabled}
        title={tooltipText}
        data-testid="new-chat-button"
      >
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
            !isDisabled
              ? "bg-gray-200 hover:bg-gray-300 active:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:active:bg-gray-500"
              : "bg-gray-300 dark:bg-gray-700"
          }`}
        >
          <Plus
            size={16}
            className={
              !isDisabled ? "text-gray-700 dark:text-white" : "text-gray-500"
            }
          />
        </div>
        {!isCollapsed && "New Chat"}
      </button>
    </div>
  );
};

export default NewChatButton;
